// SPDX-License-Identifier: AGPL-3.0-or-later

import {FeedPostCategoryResponse} from '@fluxer/schema/src/domains/feed/FeedCategorySchemas';
import {base64LengthForBytes, createBase64StringType} from '@fluxer/schema/src/primitives/FileValidators';
import {createQueryIntegerType} from '@fluxer/schema/src/primitives/QueryValidators';
import {
	createStringType,
	Int32Type,
	SnowflakeStringType,
	SnowflakeType,
} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

// Micup akis-gundem: short lived topics the system proposes and the admins publish (fixtures may publish on their
// own). A topic belongs to one category, which gives it its countries and phrase list; a post filed under a topic is
// filed under its category too. A topic lives at most FEED_TOPIC_MAX_HOURS from when it was published.
export const FEED_TOPIC_TITLE_MAX_LENGTH = 60;
export const FEED_TOPIC_DESCRIPTION_MAX_LENGTH = 140;
export const FEED_TOPIC_DEFAULT_HOURS = 24;
export const FEED_TOPIC_MAX_HOURS = 72;
export const FEED_TOPIC_DEFAULT_MAX_LIVE = 8;
// Fixture topics have their own live cap and never count toward max_live.
export const FEED_TOPIC_DEFAULT_MAX_LIVE_FIXTURES = 4;
export const FEED_TOPIC_MAX_LIVE_LIMIT = 20;
export const FEED_TOPIC_DEFAULT_MAX_PER_DAY = 20;
export const FEED_TOPIC_MAX_PER_DAY_LIMIT = 20;
export const FEED_TOPIC_TOP_MAX_OFFSET = 1000;
export const FEED_TOPIC_IMAGE_MAX_BYTES = 524_288;
// What the suggestion job may write to one day's proposal queue.
export const FEED_TOPIC_MAX_PROPOSALS_PER_DAY = 100;
export const FEED_TOPIC_PROPOSAL_MAX_SOURCE_REFS = 10;
export const FEED_TOPIC_SOURCE_REF_MAX_LENGTH = 512;
export const FEED_TOPIC_REASON_MAX_LENGTH = 280;
export const FEED_TOPIC_MAX_SOURCES = 50;
export const FEED_TOPIC_MAX_FIXTURE_LEAGUES = 20;
// The Gemini model the suggestion job asks unless the config names another; the keys are the FLUXER_GEMINI_API_KEY
// and FLUXER_API_FOOTBALL_KEY secrets (integrations.gemini / integrations.api_football), never in this config.
export const FEED_TOPIC_DEFAULT_LLM_MODEL = 'gemini-3.8-flash';

export const FEED_TOPIC_SOURCES = ['fixture', 'news', 'patch', 'feed_trend', 'manual'] as const;
export const FeedTopicSource = z
	.enum(FEED_TOPIC_SOURCES)
	.describe('Where the topic came from: a fixture, news feeds, game patch notes, rising feed terms or an admin');
export type FeedTopicSource = z.infer<typeof FeedTopicSource>;

// scheduled: published, starts later; live: takes posts; ended: past its end, read only; removed: taken down.
export const FEED_TOPIC_STATUSES = ['scheduled', 'live', 'ended', 'removed'] as const;
export const FeedTopicStatus = z.enum(FEED_TOPIC_STATUSES);
export type FeedTopicStatus = z.infer<typeof FeedTopicStatus>;

export const FEED_TOPIC_PROPOSAL_STATUSES = ['pending', 'published', 'rejected'] as const;
export const FeedTopicProposalStatus = z.enum(FEED_TOPIC_PROPOSAL_STATUSES);
export type FeedTopicProposalStatus = z.infer<typeof FeedTopicProposalStatus>;

export const FeedTopicIdParam = z.object({
	topic_id: SnowflakeType.describe('The ID of the feed topic'),
});
export type FeedTopicIdParam = z.infer<typeof FeedTopicIdParam>;

export const FeedTopicProposalIdParam = z.object({
	proposal_id: SnowflakeType.describe('The ID of the topic proposal'),
});
export type FeedTopicProposalIdParam = z.infer<typeof FeedTopicProposalIdParam>;

const ImageHash = z
	.string()
	.nullable()
	.describe(
		'Hash of the topic image, served by the media proxy at branding/feed-topic-<topic id>/<hash>.webp; null when none',
	);

export const FeedPostTopicResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the topic'),
	title: z.string().describe('The title of the topic'),
});
export type FeedPostTopicResponse = z.infer<typeof FeedPostTopicResponse>;

export const FeedTopicResponse = FeedPostTopicResponse.extend({
	description: z.string().nullable().describe('One sentence about the topic, or null'),
	image: ImageHash,
	category: FeedPostCategoryResponse.nullable().describe('The category the topic and its posts are filed under'),
	starts_at: z.string().describe('ISO8601 timestamp the topic started taking posts'),
	ends_at: z.string().describe('ISO8601 timestamp the topic stops taking posts'),
	status: z
		.enum(['live', 'ended'])
		.describe('live: takes posts; ended: read only. Removed and not yet started topics are not shown'),
	post_count: Int32Type.describe('Posts listed on the topic, for display only'),
});
export type FeedTopicResponse = z.infer<typeof FeedTopicResponse>;

export const FeedTopicListResponse = z
	.array(FeedTopicResponse)
	.max(FEED_TOPIC_MAX_LIVE_LIMIT)
	.describe("The live topics shown in the caller's country, newest first");
export type FeedTopicListResponse = z.infer<typeof FeedTopicListResponse>;

export const FeedTopicSort = z.enum(['top', 'new']);
export type FeedTopicSort = z.infer<typeof FeedTopicSort>;

export const FeedTopicPostsQuery = z.object({
	sort: FeedTopicSort.default('top').describe(
		"top: Keşfet's trend order, paged by offset; new: newest first, paged by before",
	),
	before: SnowflakeType.optional().describe('sort=new only: return posts older than this post ID'),
	offset: createQueryIntegerType({defaultValue: 0, minValue: 0, maxValue: FEED_TOPIC_TOP_MAX_OFFSET}).describe(
		`sort=top only: posts to skip (0-${FEED_TOPIC_TOP_MAX_OFFSET})`,
	),
	limit: createQueryIntegerType({defaultValue: 25, minValue: 1, maxValue: 50}).describe(
		'Maximum number of posts to return (1-50)',
	),
});
export type FeedTopicPostsQuery = z.infer<typeof FeedTopicPostsQuery>;

const TopicTitle = createStringType(1, FEED_TOPIC_TITLE_MAX_LENGTH).describe(
	`Title (1-${FEED_TOPIC_TITLE_MAX_LENGTH} characters), one sentence in the language of its category`,
);
const TopicDescription = createStringType(1, FEED_TOPIC_DESCRIPTION_MAX_LENGTH).describe(
	`One sentence (1-${FEED_TOPIC_DESCRIPTION_MAX_LENGTH} characters)`,
);
const TopicImage = createBase64StringType(1, base64LengthForBytes(FEED_TOPIC_IMAGE_MAX_BYTES)).describe(
	'Base64 PNG, JPEG or WebP image, 512 KB at most, still; SVG is refused',
);
const TopicTime = z.iso.datetime();

export const CreateFeedTopicRequest = z.object({
	title: TopicTitle,
	description: TopicDescription.nullable().default(null),
	category_id: SnowflakeType.describe('An enabled category the topic and its posts are filed under'),
	image: TopicImage.optional(),
	starts_at: TopicTime.optional().describe('ISO8601 start, at most the end; omitted: now'),
	ends_at: TopicTime.optional().describe(
		`ISO8601 end, at most ${FEED_TOPIC_MAX_HOURS} hours after publishing; omitted: the start plus the config's default_hours`,
	),
	proposal_id: SnowflakeType.optional().describe('A pending proposal this publishes; it is marked published'),
});
export type CreateFeedTopicRequest = z.infer<typeof CreateFeedTopicRequest>;

export const UpdateFeedTopicRequest = z
	.object({
		title: TopicTitle.optional(),
		description: TopicDescription.nullable().optional(),
		image: TopicImage.nullable().optional().describe('A new image, or null to drop it'),
		ends_at: TopicTime.optional().describe(
			`ISO8601 end, at most ${FEED_TOPIC_MAX_HOURS} hours after the topic was published`,
		),
		status: z.enum(['ended', 'removed']).optional().describe('ended: stops it now; removed: takes it down for good'),
	})
	.refine((value) => Object.values(value).some((field) => field !== undefined), 'At least one field is required');
export type UpdateFeedTopicRequest = z.infer<typeof UpdateFeedTopicRequest>;

const SourceId = z
	.string()
	.regex(/^[a-z0-9_-]{1,64}$/)
	.describe('A source the suggestion job knows by this ID; the addresses it reads are fixed in code');

export const FeedTopicConfig = z.object({
	max_live: z
		.number()
		.int()
		.min(1)
		.max(FEED_TOPIC_MAX_LIVE_LIMIT)
		.describe('Topics live or scheduled at the same time, fixture topics apart'),
	max_live_fixtures: z
		.number()
		.int()
		.min(1)
		.max(FEED_TOPIC_MAX_LIVE_LIMIT)
		.describe(
			'Fixture topics live or scheduled at the same time; the suggestion job keeps to it, an admin publishing a queued fixture may go past it',
		),
	max_per_day: z
		.number()
		.int()
		.min(1)
		.max(FEED_TOPIC_MAX_PER_DAY_LIMIT)
		.describe('Topics published a day (UTC), removed ones included'),
	default_hours: z.number().int().min(1).max(FEED_TOPIC_MAX_HOURS).describe('Life of a topic given no end'),
	auto_publish_fixtures: z
		.boolean()
		.describe('Fixture topics of the chosen leagues publish on their own; off, they wait in the queue'),
	fixture_category_id: SnowflakeStringType.nullable().describe('The category fixture topics go under; null: none'),
	fixture_leagues: z
		.array(z.number().int().positive())
		.max(FEED_TOPIC_MAX_FIXTURE_LEAGUES)
		.describe('API-Football league IDs whose matches become fixture topics'),
	llm_enabled: z.boolean().describe('Whether the suggestion job asks the LLM to turn news into proposals'),
	llm_model: z
		.string()
		.regex(/^[a-z0-9][a-z0-9.-]{0,63}$/)
		.describe(`The Gemini model the suggestion job asks (${FEED_TOPIC_DEFAULT_LLM_MODEL} by default)`),
	sources: z
		.array(z.object({id: SourceId, enabled: z.boolean()}))
		.max(FEED_TOPIC_MAX_SOURCES)
		.describe('Sources turned on or off; one not listed keeps its default in code'),
});
export type FeedTopicConfig = z.infer<typeof FeedTopicConfig>;

// The stored value with the defaults filled in, for a store that has none yet or an older shape.
export const StoredFeedTopicConfig = FeedTopicConfig.extend({
	max_live: FeedTopicConfig.shape.max_live.default(FEED_TOPIC_DEFAULT_MAX_LIVE),
	max_live_fixtures: FeedTopicConfig.shape.max_live_fixtures.default(FEED_TOPIC_DEFAULT_MAX_LIVE_FIXTURES),
	max_per_day: FeedTopicConfig.shape.max_per_day.default(FEED_TOPIC_DEFAULT_MAX_PER_DAY),
	default_hours: FeedTopicConfig.shape.default_hours.default(FEED_TOPIC_DEFAULT_HOURS),
	auto_publish_fixtures: FeedTopicConfig.shape.auto_publish_fixtures.default(true),
	fixture_category_id: FeedTopicConfig.shape.fixture_category_id.default(null),
	fixture_leagues: FeedTopicConfig.shape.fixture_leagues.default([]),
	llm_enabled: FeedTopicConfig.shape.llm_enabled.default(false),
	llm_model: FeedTopicConfig.shape.llm_model.default(FEED_TOPIC_DEFAULT_LLM_MODEL),
	sources: FeedTopicConfig.shape.sources.default([]),
});

export const AdminFeedTopicResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the topic'),
	title: z.string(),
	description: z.string().nullable(),
	image: ImageHash,
	category_id: SnowflakeStringType,
	starts_at: z.string().describe('ISO8601 timestamp'),
	ends_at: z.string().describe('ISO8601 timestamp'),
	status: FeedTopicStatus,
	source: FeedTopicSource,
	source_ref: z.string().nullable().describe('The source item the topic came from (a fixture ID or a link), or null'),
	created_by: SnowflakeStringType.nullable().describe('The admin who published it; null when the system did'),
	created_at: z.string().describe('ISO8601 timestamp'),
	post_count: Int32Type,
});
export type AdminFeedTopicResponse = z.infer<typeof AdminFeedTopicResponse>;

export const AdminFeedTopicsResponse = z.object({
	topics: z
		.array(AdminFeedTopicResponse)
		.max(4 * FEED_TOPIC_MAX_PER_DAY_LIMIT)
		.describe('Topics published in the last four days, every status, newest first'),
	config: FeedTopicConfig,
});
export type AdminFeedTopicsResponse = z.infer<typeof AdminFeedTopicsResponse>;

export const AdminFeedTopicProposalsQuery = z.object({
	date: z.iso.date().optional().describe('The UTC day (YYYY-MM-DD) whose queue to read; omitted: today'),
});
export type AdminFeedTopicProposalsQuery = z.infer<typeof AdminFeedTopicProposalsQuery>;

export const FeedTopicProposalResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the proposal'),
	title: z.string(),
	description: z.string().nullable(),
	category_id: SnowflakeStringType.nullable().describe('The category proposed; null when none fit'),
	source: FeedTopicSource,
	source_refs: z
		.array(z.string())
		.max(FEED_TOPIC_PROPOSAL_MAX_SOURCE_REFS)
		.describe('The items behind it: links of the headlines, a fixture ID'),
	reason: z.string().nullable().describe("The suggestion job's short reason, or null"),
	sensitive: z
		.boolean()
		.describe('Flagged by the LLM or matching a category phrase: never published on its own, check it first'),
	status: FeedTopicProposalStatus,
	starts_at: z.string().nullable().describe('ISO8601 start a fixture proposal brings, or null'),
	ends_at: z.string().nullable().describe('ISO8601 end a fixture proposal brings, or null'),
	topic_id: SnowflakeStringType.nullable().describe('The topic published from it, or null'),
	created_at: z.string().describe('ISO8601 timestamp'),
});
export type FeedTopicProposalResponse = z.infer<typeof FeedTopicProposalResponse>;

export const AdminFeedTopicProposalsResponse = z.object({
	proposals: z
		.array(FeedTopicProposalResponse)
		.max(FEED_TOPIC_MAX_PROPOSALS_PER_DAY)
		.describe("The day's proposals, newest first"),
});
export type AdminFeedTopicProposalsResponse = z.infer<typeof AdminFeedTopicProposalsResponse>;
